import { useCallback, useEffect, useRef, useState } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import {
  TabList,
  Tab,
  CounterBadge,
  Button,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import { Dashboard } from './pages/Dashboard';
import { RequestForm } from './pages/RequestForm';
import { ReviewerQueue } from './pages/ReviewerQueue';
import { AdminPanel } from './pages/AdminPanel';
import { getSession, type Session } from './api/session';
import { useResponsiveStyles } from './hooks/useResponsiveStyles';
import { useTeams } from './teams/TeamsProvider';
import { OpenInTeamsBanner } from './components/OpenInTeamsBanner';
import { EmptyState } from './components/EmptyState';
import { SPACE_X } from './styles/scale';
import { useLayoutStyles } from './styles/surfaces';

/** Badge text is capped, same as the visual "99+" CounterBadge already shows. */
const BADGE_OVERFLOW = 99;

const useStyles = makeStyles({
  /**
   * The tab strip is laid on the SAME horizontal gutter as the page content
   * below it (#202). Before this it sat flush at x=0 while `<main>` carried its
   * own padding, so the first tab and the page's `<h1>` started at different x
   * positions and nothing in the shell lined up.
   *
   * ── Why the values are written out rather than composed from `page` ──
   * `useLayoutStyles().page` is the definition of the gutter (24px desktop,
   * 16px below the breakpoint) and `<main>` uses it directly. It cannot be
   * reused here, because the tab strip wants the HORIZONTAL half only: a 24px
   * band above and below the tabs would spend the scarcest resource an iframe
   * has on chrome, and Fluent's own `Tab` already brings the height its touch
   * target needs. Griffel emits `page`'s `padding` shorthand as ONE atomic
   * class (measured: `.fcnpsh9 { padding: var(--spacingVerticalXXL) }`), which
   * `mergeClasses` cannot deduplicate against `paddingTop`/`paddingBottom`
   * longhands — so a vertical reset layered on top would win or lose purely on
   * stylesheet insertion order, i.e. on which hook happened to render first.
   * `App.test.tsx` pins the tab strip's gutter to `<main>`'s.
   */
  tabs: {
    paddingInlineStart: SPACE_X.xxl,
    paddingInlineEnd: SPACE_X.xxl,
    '@media (max-width: 700px)': {
      paddingInlineStart: SPACE_X.l,
      paddingInlineEnd: SPACE_X.l,
    },
  },
  badge: { marginInlineStart: SPACE_X.s },
});

/**
 * Drives the app shell's role-based tabs (wireframe sheet 10, C1). Absent on
 * failure rather than defaulting to any role — a broken session call must
 * never grant a tab it shouldn't, only ever fail closed to "requester only".
 */
function useSession(): Session | null {
  const [session, setSession] = useState<Session | null>(null);

  const load = useCallback(async () => {
    try {
      setSession(await getSession());
    } catch {
      setSession(null);
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = setInterval(() => void load(), 60_000);
    window.addEventListener('focus', load as () => void);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', load as () => void);
    };
  }, [load]);

  return session;
}

/**
 * The request form's own routes (`/requests/new`, `/requests/:id`) have no
 * `Tab` of their own — every wireframe frame that shows the form still draws
 * My Requests as the active tab, since that is where the form was opened
 * from and where Close/Save & close return to. Without this, `TabList`'s
 * `selectedValue` never matches any `Tab value`, so the indicator disappears
 * entirely while the form is open.
 */
function activeTabValue(pathname: string): string {
  if (pathname.startsWith('/requests')) return '/';
  // `/review/:id/edit` belongs to the Review tab.
  if (pathname.startsWith('/review')) return '/review';
  return pathname;
}

/**
 * The `path="*"` fallback (#202, wireframe 10.2 `not-found`).
 *
 * Until now an unknown hash — a stale deep link, a request id someone typed by
 * hand, a bookmark from before a route was renamed — matched no `<Route>` at
 * all and `<Routes>` rendered nothing, so the tab showed the shell above an
 * empty canvas. That is indistinguishable from a page that failed to load, and
 * it strands the user with no way back except the tab strip they may not read
 * as navigation.
 *
 * The way out navigates rather than linking to a URL: this app runs under
 * `HashRouter`, so an `href` would have to encode the `#/` prefix here and
 * would then be wrong under the `MemoryRouter` the tests mount.
 */
/**
 * #226 — a Teams activity-feed notification's preview text or email carries a
 * deep link Teams encodes as `context={subEntityId:...}` (`teamsDeepLink()`,
 * `apps/api/src/integrations/graph/notifications.ts`); the app never opens
 * `topic.webUrl` directly for that (Graph rejects any `webUrl` with a query
 * string), but tapping the deep link passes that same id back to this tab as
 * `context.page.subPageId` (`microsoftTeams.app.getContext()`, already read
 * by `TeamsProvider`). Nothing consumed it before this — the tab always
 * booted to whatever hash route was already in the URL, which a fresh deep
 * link never seeds, so it landed on the Dashboard regardless.
 *
 * Fires at most once per app session, guarded by a ref rather than relying on
 * `context` never changing identity again — `TeamsProvider` only sets
 * `context` once (on init), but a ref makes that guarantee local rather than
 * borrowed. A stale or no-longer-visible request id is not validated here:
 * `/requests/:id` already has to handle a 403/404 for a request the viewer
 * can no longer see (`ApiErrorMessage.tsx`), which is the same failure mode
 * as a stale deep link.
 */
function useDeepLinkNavigation(): void {
  const { context } = useTeams();
  const navigate = useNavigate();
  const navigated = useRef(false);

  useEffect(() => {
    if (navigated.current) return;
    const subPageId = context?.page.subPageId;
    if (typeof subPageId !== 'string' || subPageId.length === 0) return;
    navigated.current = true;
    void navigate(`/requests/${subPageId}`);
  }, [context, navigate]);
}

function NotFoundRoute({ dashboardLabel }: { dashboardLabel: string }) {
  const navigate = useNavigate();

  return (
    <EmptyState
      variant="not-found"
      title="We couldn’t find that page"
      body="The link may be out of date, or the request it pointed at may have been removed."
      action={
        <Button appearance="primary" onClick={() => void navigate('/')}>
          Go to {dashboardLabel}
        </Button>
      }
    />
  );
}

export function App() {
  const styles = useStyles();
  const layout = useLayoutStyles();
  const responsive = useResponsiveStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  useDeepLinkNavigation();

  const pendingCount = session?.pendingReviewCount ?? 0;
  const hasBadge = session?.isReviewer === true && pendingCount > 0;
  const badgeText = pendingCount > BADGE_OVERFLOW ? `${BADGE_OVERFLOW}+` : String(pendingCount);
  const reviewerQueueLabel = hasBadge ? `Reviewer Queue, ${badgeText} pending` : 'Reviewer Queue';
  // #222/#224 — for a reviewer OR an admin, "My Requests" becomes "Dashboard":
  // same tab, same route, re-scoped and read-only rather than a separate
  // third tab. An admin who is also a reviewer still gets the Reviewer Queue
  // tab separately (below) to actually act as a reviewer — this only decides
  // what the shared Dashboard tab itself shows, and admin wins there since
  // it's the broader superset (org-wide vs. the reviewer's own scope).
  const isReviewer = session?.isReviewer === true;
  const isAdmin = session?.isAdmin === true;
  const dashboardLabel = isReviewer || isAdmin ? 'Dashboard' : 'My Requests';
  const dashboardMode = isAdmin ? 'admin' : isReviewer ? 'review' : 'mine';
  // #243 — every admin has full reviewer capability, unconditionally (no
  // setting): an admin who isn't already a real reviewer gets the real
  // ReviewerQueue under the SAME Dashboard tab, in place of the read-only
  // admin Dashboard, rather than a second, differently-scoped read-only
  // list. A real reviewer who is also an admin keeps today's behaviour
  // unchanged (dashboardMode already resolves to 'review' for them) — this
  // only ever applies to an admin who is NOT already a reviewer, so it can
  // never produce a redundant or conflicting surface.
  const showReviewerQueueOnDashboard = isAdmin && !isReviewer;

  // No wrapper element of its own: the canvas fill (`min-height: 100vh` and
  // `colorNeutralBackground2`) belongs to `TeamsProvider`'s own fill div and
  // must live on exactly one element. Painting it here as well made the tab
  // visibly change shade the moment `App` mounted over the boot spinner.
  return (
    <>
      <TabList
        className={mergeClasses(styles.tabs, responsive.touchTargets)}
        selectedValue={activeTabValue(location.pathname)}
        onTabSelect={(_, data) => {
          // react-router 7's navigate() returns a promise; the handler must
          // return void, so discard it explicitly rather than leaking it.
          void navigate(String(data.value));
        }}
      >
        <Tab value="/">{dashboardLabel}</Tab>
        {session?.isReviewer && (
          <Tab value="/review" aria-label={reviewerQueueLabel}>
            Reviewer Queue
            {hasBadge && (
              <CounterBadge
                className={styles.badge}
                count={pendingCount}
                overflowCount={BADGE_OVERFLOW}
                size="small"
              />
            )}
          </Tab>
        )}
        {session?.isAdmin && <Tab value="/admin">Admin</Tab>}
      </TabList>
      {/* #189 — this is a Teams tab, not a centred marketing page: the
          Dashboard's own desktop table (wireframe R1) must fill the available
          tab canvas at every viewport rather than leaving empty margins on a
          wide screen, so `layout.page` caps nothing and adds only the gutter.
          #190's Reviewer Queue table (wireframe sheet 05, V1) relies on the
          same full-width shell. */}
      <main className={layout.page}>
        <OpenInTeamsBanner />
        <Routes>
          <Route
            path="/"
            element={
              showReviewerQueueOnDashboard ? <ReviewerQueue /> : <Dashboard mode={dashboardMode} />
            }
          />
          {/* Keyed by pathname: without a key, react-router reuses the same
              RequestForm instance across /requests/new -> /requests/:id (and
              between two different ids), so its local state — "submitted",
              the loaded draft — never resets. "View request" on the just-
              submitted confirmation screen would navigate the URL but keep
              rendering that same confirmation screen forever. */}
          <Route
            path="/requests/new"
            element={<RequestForm key={location.pathname} viewerAadId={session?.aadId ?? null} />}
          />
          <Route
            path="/requests/:id"
            element={<RequestForm key={location.pathname} viewerAadId={session?.aadId ?? null} />}
          />
          <Route
            path="/review/:id/edit"
            element={
              <RequestForm
                key={location.pathname}
                mode="review"
                viewerAadId={session?.aadId ?? null}
              />
            }
          />
          <Route path="/review" element={<ReviewerQueue />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="*" element={<NotFoundRoute dashboardLabel={dashboardLabel} />} />
        </Routes>
      </main>
    </>
  );
}
